import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { articleContent } from '@/mocks/articleContent';
import { mainSiteUrl } from '@/mocks/categories';
import { SITE_URL } from '@/constants/site';

const faqSection = articleContent.sections.find((s) => s.id === 'faq');
const faqItems = faqSection?.faqItems ?? [];

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: articleContent.title,
  description: articleContent.metaDescription,
  author: {
    '@type': 'Person',
    name: articleContent.author,
  },
  datePublished: articleContent.publishedDate,
  dateModified: articleContent.modifiedDate,
  publisher: {
    '@type': 'Organization',
    name: 'Kakobuy Finds',
    url: SITE_URL + '/',
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': SITE_URL + '/kakobuy-spreadsheet-guide',
  },
  wordCount: articleContent.sections.reduce(
    (count, s) => count + (s.content ? s.content.split(' ').length : 0),
    0
  ),
};

const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_URL + '/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Guides',
      item: SITE_URL + '/kakobuy-spreadsheet-guide',
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: articleContent.title,
    },
  ],
};

export default function ArticlePage() {
  const { title, publishedDate, modifiedDate, author, readTime, category, sections } = articleContent;

  const tocItems = sections.map((s) => ({ id: s.id, heading: s.heading }));

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
      />

      <Header />

      <main className="pt-20 pb-16">
        <article className="max-w-3xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  Home
                </Link>
              </li>
              <li>
                <span className="mx-1">/</span>
              </li>
              <li>
                <Link to="/kakobuy-spreadsheet-guide" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                  Guides
                </Link>
              </li>
              <li>
                <span className="mx-1">/</span>
              </li>
              <li className="text-foreground-600 whitespace-nowrap">
                {title.length > 50 ? title.slice(0, 50) + '...' : title}
              </li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-medium px-3 py-1 bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                {category}
              </span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{readTime}</span>
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading text-foreground-950 leading-tight mb-6">
              {title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
              <span className="whitespace-nowrap">By {author}</span>
              <span className="text-foreground-300">|</span>
              <span className="whitespace-nowrap">Published: {publishedDate}</span>
              <span className="text-foreground-300">|</span>
              <span className="whitespace-nowrap">Updated: {modifiedDate}</span>
            </div>
          </header>

          <div className="bg-background-100 rounded-2xl p-5 md:p-7 mb-10">
            <h2 className="text-sm font-semibold text-foreground-800 uppercase tracking-wider mb-4">
              Table of Contents
            </h2>
            <nav aria-label="Table of Contents">
              <ol className="space-y-2">
                {tocItems.map((item, i) => (
                  <li key={item.id} className="flex items-start gap-2">
                    <span className="text-xs text-primary-500 font-medium mt-0.5 whitespace-nowrap">
                      {String(i + 1).padStart(2, '0')}.
                    </span>
                    <a
                      href={`#${item.id}`}
                      className="text-sm text-foreground-600 hover:text-primary-500 transition-colors leading-relaxed"
                    >
                      {item.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>

          <div className="prose-custom space-y-10">
            {sections.map((section) => (
              <section key={section.id} id={section.id}>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.content && (
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {section.content}
                  </p>
                )}
                {section.faqItems && (
                  <div className="space-y-4 mt-4">
                    {section.faqItems.map((faq, fi) => (
                      <details
                        key={fi}
                        className="bg-background-100 rounded-xl p-4 md:p-5 group cursor-pointer"
                      >
                        <summary className="text-sm md:text-base font-medium text-foreground-800 list-none flex items-center justify-between gap-3">
                          {faq.question}
                          <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                          <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                        </summary>
                        <p className="text-sm text-foreground-600 leading-relaxed mt-3 pt-3 border-t border-background-200/50">
                          {faq.answer}
                        </p>
                      </details>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>

          <div className="mt-12 p-6 md:p-8 bg-primary-50/50 rounded-2xl border border-primary-100/60 text-center">
            <h3 className="text-lg font-heading text-foreground-950 mb-2">
              Ready to Start Exploring?
            </h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              Open the full Kakobuy Spreadsheet and begin discovering products organized just for you.
            </p>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="group inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
            </a>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-background-200/50">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Browse Kakobuy Sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}